import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  MatPaginator,
  MatPaginatorIntl,
  PageEvent,
} from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, firstValueFrom, of, Subject, takeUntil } from 'rxjs';
import { PicnicService } from 'src/app/services/api/picnic.service';
import { CognitoService } from 'src/app/services/cognito.service';
import Swal from 'sweetalert2';
import { DialogPicnicDetailsComponent } from './dialog-picnic-details/dialog-picnic-details.component';
import { DialogProviderMultipleComponent } from './dialog-provider-multiple/dialog-provider-multiple.component';
import {
  ConsecutiveSnackWarning,
  CuadroPicnicFilterCriteria,
  CuadroPicnicItem,
  CuadroPicnicSummaryMetrics,
  PassengerDietaryRestrictionDetail,
  PicnicDetailsGroup,
  PicnicOperationalServiceSummary,
  PicnicServiceDetailItem,
  TIPO_ALIMENTO,
  TIPO_ALIMENTOS_LIST,
} from './models/cuadro-picnic.model';
import { ServiciosOperativosModalComponent } from './servicios-operativos-modal/servicios-operativos-modal.component';
import {
  DISPLAYED_TABLE_COLUMNS,
  extractPassengerNamePrefix,
  extractPassengerRestrictionsBody,
  filterValidDietaryRestrictions,
  formatDateRangeFilterLabel,
  formatDietaryRestrictionsTooltipText,
  formatLocalDateToDisplay,
  formatLocalDateToYMD,
  getDietaryBadgeClass,
  getDietaryBadgeIcon,
  getDistinctDietaryRestrictionTags,
  hasValidDietaryRestrictions,
  isCriticalDietaryRestriction,
  OperationalPicnicServiceCategory,
  OperationalServiceModalType,
  ORDER_REVIEW_MESSAGES,
  PAGINATOR_SPANISH_LABELS,
  parsePassengerDietaryRestrictions,
  hasOperationalSummaryPreliminary,
  isOperationalSummaryFullyConfirmed,
  resolveOperationalServiceMetadata,
  resolveServicePillStatusClass,
  resolveServicePillTooltip,
  synchronizeOperationalServiceSummary,
} from './utils';

/**
 * Factory function creating a Spanish-localized `MatPaginatorIntl` configuration
 * for Cuadro Picnic table pagination controls.
 *
 * @returns Configured `MatPaginatorIntl` instance with Spanish labels and range formatting.
 */
export function getSpanishPaginatorIntl(): MatPaginatorIntl {
  const customPaginatorIntl = new MatPaginatorIntl();
  customPaginatorIntl.itemsPerPageLabel = PAGINATOR_SPANISH_LABELS.itemsPerPage;
  customPaginatorIntl.nextPageLabel = PAGINATOR_SPANISH_LABELS.nextPage;
  customPaginatorIntl.previousPageLabel = PAGINATOR_SPANISH_LABELS.previousPage;
  customPaginatorIntl.firstPageLabel = PAGINATOR_SPANISH_LABELS.firstPage;
  customPaginatorIntl.lastPageLabel = PAGINATOR_SPANISH_LABELS.lastPage;
  customPaginatorIntl.getRangeLabel = (
    pageIndex: number,
    pageSize: number,
    totalLength: number,
  ): string => {
    if (totalLength === 0 || pageSize === 0) {
      return `0 de ${totalLength}`;
    }
    const safeTotalLength = Math.max(totalLength, 0);
    const startIndex = pageIndex * pageSize;
    const endIndex =
      startIndex < safeTotalLength
        ? Math.min(startIndex + pageSize, safeTotalLength)
        : startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} de ${safeTotalLength}`;
  };
  return customPaginatorIntl;
}

/**
 * Backward-compatible alias for paginator factory.
 */
export const getSpanishCuadroPicnicPaginatorIntl = getSpanishPaginatorIntl;

/**
 * Controller component for Cuadro Picnic.
 * Manages table rendering, GraphQL API fetching, filters, consecutive snack warnings, KPI summary metrics, and modal dialogs.
 */
@Component({
  selector: 'app-cuadro-picnic',
  templateUrl: './cuadro-picnic.component.html',
  styleUrls: ['./cuadro-picnic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getSpanishPaginatorIntl,
    },
  ],
})
export class CuadroPicnicComponent implements OnInit, OnDestroy {
  private readonly _unsubscribeOnDestroy$: Subject<boolean> =
    new Subject<boolean>();

  public isLoadingData: boolean = false;
  public isFiltering: boolean = false;
  public errorMessageText: string | null = null;
  public paginationNextCursor: string | undefined = undefined;

  public pageIndex: number = 0;
  public pageSize: number = 10;
  public pageSizeOptions: number[] = [10, 25, 50];
  public pageCursors: Array<string | null> = [null];
  public totalServerRecords: number = 0;
  public readonly skeletonRows: null[] = Array(8).fill(null);

  /**
   * Fixed number of files requested per server batch query to keep backend unchanged.
   */
  private readonly serverBatchLimit: number = 10;

  /**
   * Monotonically increasing execution ID to cancel and discard obsolete asynchronous load loops.
   */
  private activeLoadExecutionId: number = 0;

  /**
   * Maximum number of consecutive 10-file batches fetched in a single sequential fill operation.
   * Acts as a circuit breaker preventing infinite loops if files contain no picnic services.
   */
  private readonly maxConsecutiveBatches: number = 5;

  /**
   * Calculates the paginator length dynamically for hybrid buffer pagination.
   * If there is a next cursor on the server, reports an extra page size so the
   * Next button remains enabled even when local buffer rows are partially filled.
   */
  public get paginatorLength(): number {
    const totalBuffered = this.masterPicnicItems.length;
    if (this.paginationNextCursor) {
      return (
        Math.max(totalBuffered, (this.pageIndex + 1) * this.pageSize) +
        this.pageSize
      );
    }
    return totalBuffered;
  }

  /**
   * Subject to cancel previous in-flight load requests when filters or search change.
   */
  private readonly cancelPendingLoad$ = new Subject<void>();

  /**
   * Clears any active search debounce timeout.
   */
  private clearPendingSearchDebounce(): void {
    if (this.searchDebounceTimeoutId) {
      clearTimeout(this.searchDebounceTimeoutId);
      this.searchDebounceTimeoutId = null;
    }
  }

  public masterPicnicItems: CuadroPicnicItem[] = [];
  public tableDataSource = new MatTableDataSource<CuadroPicnicItem>([]);

  /**
   * Set tracking row IDs that are currently executing an order review GraphQL mutation.
   */
  public readonly updatingRowReviewIdentifiersSet = new Set<string>();

  public summaryMetricsRecord: CuadroPicnicSummaryMetrics = {
    snackCount: 0,
    picnicCount: 0,
    boxLunchCount: 0,
    trekInkaTrailCount: 0,
    trekAusangateCount: 0,
    trekSalkantayCount: 0,
    hiddenValleyCount: 0,
    donativoCount: 0,
  };

  public consecutiveSnackAlertList: ConsecutiveSnackWarning[] = [];
  public isSnackAlertBannerDismissed: boolean = false;
  public isSnackAlertListExpanded: boolean = true;
  public activeAlertFilterFileNumber: string | null = null;

  public availableYearsList: string[] = [
    '2024',
    '2025',
    '2026',
    '2027',
    '2028',
  ];
  public searchValidationMessage: string = '';
  private searchDebounceTimeoutId: any = null;

  public filterCriteriaRecord: CuadroPicnicFilterCriteria = {
    searchKeyword: '',
    startDate: null,
    endDate: null,
    selectedYear: new Date().getFullYear().toString(),
    selectedServiceType: '',
  };

  public availableServiceTypesList: string[] = [...TIPO_ALIMENTOS_LIST];

  public readonly OperationalServiceCategory = OperationalPicnicServiceCategory;

  public displayedTableColumns: string[] = [...DISPLAYED_TABLE_COLUMNS];

  public isLegendExpanded: boolean = false;

  @ViewChild(MatPaginator) public matPaginatorComponent!: MatPaginator;
  @ViewChild(MatSort) public matSortComponent!: MatSort;

  /**
   * Initializes component dependencies.
   *
   * @description Directs component instantiation with injected Angular services.
   * @param {ChangeDetectorRef} changeDetectorRef - Reference to control change detection strategy.
   * @param {PicnicService} picnicService - GraphQL API service for Cuadro Picnic data.
   * @param {CognitoService} cognitoService - AWS Cognito authentication service for user identity.
   * @param {MatDialog} dialogService - Material Dialog service for opening modal popups.
   * @param {MatSnackBar} snackBarNotificationService - Notification service for user feedback.
   * @param {MatPaginatorIntl} paginatorIntl - Material paginator internationalization service.
   */
  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly picnicService: PicnicService,
    private readonly cognitoService: CognitoService,
    public readonly dialogService: MatDialog,
    private readonly snackBarNotificationService: MatSnackBar,
    private readonly paginatorIntl: MatPaginatorIntl,
  ) {}

  /**
   * Angular Lifecycle hook executed on component initialization.
   *
   * @returns {void}
   */
  public ngOnInit(): void {
    this.configurePaginatorIntl();
    this.loadCuadroPicnic();
  }

  /**
   * Configures the Spanish paginator label formatter to accurately report
   * real buffered items in memory and dynamic server cursor availability.
   *
   * @returns {void}
   */
  private configurePaginatorIntl(): void {
    this.paginatorIntl.itemsPerPageLabel = 'Filas por página:';
    this.paginatorIntl.nextPageLabel = 'Página siguiente';
    this.paginatorIntl.previousPageLabel = 'Página anterior';
    this.paginatorIntl.firstPageLabel = 'Primera página';
    this.paginatorIntl.lastPageLabel = 'Última página';
    this.paginatorIntl.getRangeLabel = (
      pageIndex: number,
      pageSize: number,
      _totalLength: number,
    ): string => {
      const totalBuffered = this.masterPicnicItems.length;
      if (totalBuffered === 0 || pageSize === 0) {
        return `0 de 0`;
      }
      const startIndex = pageIndex * pageSize;
      if (startIndex >= totalBuffered) {
        return `0 de ${totalBuffered}`;
      }
      const endIndex = Math.min(startIndex + pageSize, totalBuffered);
      return this.paginationNextCursor
        ? `${startIndex + 1} - ${endIndex} de más de ${totalBuffered}`
        : `${startIndex + 1} - ${endIndex} de ${totalBuffered}`;
    };
  }

  /**
   * Lifecycle cleanup hook to prevent active stream leaks.
   *
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.clearPendingSearchDebounce();
    this.cancelPendingLoad$.next();
    this.cancelPendingLoad$.complete();
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  /**
   * Fetches picnic data records from GraphQL API with graceful fallback to mock service.
   * Supports server-side cursor pagination when isAppendNextPage is set to true.
   *
   * Description:
   *   Triggers retrieval of Cuadro Picnic records according to active filter criteria,
   *   invoking the core loadCuadroPicnic logic.
   *
   * Parameters:
   *   isAppendNextPage: boolean - When true, appends the next page of records using paginationNextCursor.
   *
   * Return Value:
   *   void - No return value; updates component state and change detection.
   *
   * Exceptions & Errors:
   *   Handles API errors gracefully by falling back to mock dataset.
   */
  public loadCuadroPicnicData(): void {
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
  }

  /**
   * Handles mat-paginator page and pageSize change events using hybrid buffer pagination.
   *
   * @param event - Material paginator PageEvent containing pageIndex and pageSize.
   */
  public onPaginatorChange(event: PageEvent): void {
    if (this.isLoadingData || this.isFiltering) {
      return;
    }

    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      this.pageIndex = 0;
      if (this.matPaginatorComponent) {
        this.matPaginatorComponent.pageIndex = 0;
      }
      const targetNeededRows = this.pageSize;
      if (
        this.masterPicnicItems.length < targetNeededRows &&
        this.paginationNextCursor
      ) {
        void this.loadCuadroPicnic({
          resetPage: false,
          append: true,
          cursor: this.paginationNextCursor,
          targetNeededRows,
          isFiltering: true,
        });
        return;
      }
      this.refreshTableSlice();
      return;
    }

    const targetPageIndex = event.pageIndex;
    const targetNeededRows = (targetPageIndex + 1) * this.pageSize;

    // If buffer already has enough rows to completely fill the target page,
    // OR if the server has no more files (end of dataset), slice from buffer immediately (0ms latency)
    if (
      this.masterPicnicItems.length >= targetNeededRows ||
      !this.paginationNextCursor
    ) {
      this.pageIndex = targetPageIndex;
      this.refreshTableSlice();
      return;
    }

    // Otherwise, buffer rows are fewer than needed for a full page AND nextCursor exists on server:
    // fetch consecutive batches sequentially until buffer satisfies targetNeededRows to complete the page
    this.pageIndex = targetPageIndex;
    void this.loadCuadroPicnic({
      resetPage: false,
      append: true,
      cursor: this.paginationNextCursor,
      targetNeededRows,
      isFiltering: true,
    });
  }

  /**
   * Fetches picnic data records from GraphQL API with graceful fallback to mock service.
   * Supports server-side cursor pagination when isAppendNextPage is set to true.
   *
   * Description:
   *   Builds query filter criteria from component state and executes the GraphQL API query,
   *   populating table rows, KPI summaries, and alert banners.
   *
  /**
   * Formats a Date object or date-like string into a clean 'YYYY-MM-DD' string based on local calendar values.
   *
   * Description:
   *   Extracts local getFullYear, getMonth, and getDate to avoid timezone offset discrepancies
   *   caused by toISOString() or UTC conversions.
   *
   * Parameters:
   *   targetDateValue: Date | string | null | undefined - The source date representation.
   *
   * Return Value:
   *   string | undefined - Formatted 'YYYY-MM-DD' date string or undefined if invalid/empty.
   *
   * Exceptions & Errors:
   *   Handles invalid Date objects gracefully by returning undefined.
   */
  public formatLocalDateToYMD(
    targetDateValue: Date | string | null | undefined,
  ): string | undefined {
    return formatLocalDateToYMD(targetDateValue) || undefined;
  }

  /**
   * Formats a Date object or date-like string into a user-friendly 'DD/MM/YYYY' display format.
   * Delegated to shared date-formatting utility.
   *
   * @param targetDateValue - The source date representation.
   * @returns Formatted 'DD/MM/YYYY' string or '-' if invalid/empty.
   */
  public formatLocalDateToDisplay(
    targetDateValue: Date | string | null | undefined,
  ): string {
    return formatLocalDateToDisplay(targetDateValue);
  }

  /**
   * Tracks the last executed start date string in YYYY-MM-DD format to prevent duplicate requests.
   */
  private lastQueriedStartDate: string | undefined = undefined;

  /**
   * Tracks the last executed end date string in YYYY-MM-DD format to prevent duplicate requests.
   */
  private lastQueriedEndDate: string | undefined = undefined;

  /**
   * Handles completion of date range selection when the datepicker dialog closes.
   *
   * Description:
   *   Fires exactly once when the calendar popup closes after the user has selected both dates
   *   or closed the picker. Ensures that clicking the first date does not trigger an intermediate request.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleDatePickerClosed(): void {
    this.clearPendingSearchDebounce();
    const { startDate, endDate } = this.filterCriteriaRecord;

    // Normalize dates if start > end
    if (startDate && endDate) {
      const parsedStart = new Date(startDate);
      const parsedEnd = new Date(endDate);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        if (parsedStart > parsedEnd) {
          this.filterCriteriaRecord.startDate = endDate;
          this.filterCriteriaRecord.endDate = startDate;
        }
      }
    } else if (startDate && !endDate) {
      // If user closed after selecting only a single date, treat it as a single-day query
      this.filterCriteriaRecord.endDate = startDate;
    }

    const currentFormattedStart = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.startDate,
    );
    const currentFormattedEnd = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.endDate,
    );

    // Only trigger query if the dates actually changed from the last executed request
    if (
      currentFormattedStart !== this.lastQueriedStartDate ||
      currentFormattedEnd !== this.lastQueriedEndDate
    ) {
      this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    }
  }

  /**
   * Handles date change event on the end date input for manual typing scenarios.
   *
   * Description:
   *   When typing manually without opening the calendar picker, triggers the query once
   *   both startDate and endDate are filled and verified.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleEndDateInputChange(): void {
    this.clearPendingSearchDebounce();
    const { startDate, endDate } = this.filterCriteriaRecord;
    if (startDate && endDate) {
      const currentFormattedStart = this.formatLocalDateToYMD(startDate);
      const currentFormattedEnd = this.formatLocalDateToYMD(endDate);
      if (
        currentFormattedStart !== this.lastQueriedStartDate ||
        currentFormattedEnd !== this.lastQueriedEndDate
      ) {
        this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
      }
    }
  }

  /**
   * Clears the active date range filter criteria and reloads the table.
   *
   * Description:
   *   Resets startDate and endDate to null and triggers data reload.
   *
   * Parameters:
   *   clickEvent: Event - The click event object.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public clearDateRangeFilter(clickEvent: Event): void {
    clickEvent.stopPropagation();
    this.clearPendingSearchDebounce();
    this.filterCriteriaRecord.startDate = null;
    this.filterCriteriaRecord.endDate = null;
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
  }

  /**
   * Evaluates if a search string is eligible to query the backend.
   * If search is purely numeric (file number), requires at least 4 digits.
   *
   * @param rawSearch - The raw search input string.
   * @returns boolean - True if eligible to be sent to backend.
   */
  public canSendSearch(rawSearch: string): boolean {
    if (!rawSearch) return false;
    const isNumericSearch = /^\d+$/.test(rawSearch);
    return !isNumericSearch || rawSearch.length >= 4;
  }

  /**
   * Updates the search validation message when user types short numeric searches.
   */
  private updateSearchValidationMessage(): void {
    const rawSearch = (this.filterCriteriaRecord.searchKeyword || '').trim();
    const isShortNumericSearch =
      /^\d+$/.test(rawSearch) && rawSearch.length > 0 && rawSearch.length < 4;
    this.searchValidationMessage = isShortNumericSearch
      ? 'Ingresa al menos 4 dígitos para buscar por número de file'
      : '';
  }

  /**
   * Handles user typing in the general search box with 400ms debounce.
   *
   * @param value - Search string from template input.
   */
  public onSearchKeywordChange(value: string): void {
    this.filterCriteriaRecord.searchKeyword = value;
    this.updateSearchValidationMessage();
    this.changeDetectorRef.markForCheck();

    this.clearPendingSearchDebounce();

    // Cancel any current in-flight query so old search results do not arrive late
    this.cancelPendingLoad$.next();

    const trimmedValue = (value || '').trim();
    if (
      this.activeAlertFilterFileNumber &&
      trimmedValue !== this.activeAlertFilterFileNumber
    ) {
      this.activeAlertFilterFileNumber = null;
    }

    if (
      /^\d+$/.test(trimmedValue) &&
      trimmedValue.length > 0 &&
      trimmedValue.length < 4
    ) {
      this.isFiltering = false;
      this.isLoadingData = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.searchDebounceTimeoutId = setTimeout(() => {
      this.searchDebounceTimeoutId = null;
      this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    }, 400);
  }

  /**
   * Handles year filter changes from the primary filters bar.
   *
   * @param newYear - Selected year string (e.g. '2026').
   */
  public onYearChange(newYear?: string): void {
    this.clearPendingSearchDebounce();
    if (newYear) {
      this.filterCriteriaRecord.selectedYear = newYear;
    }
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
  }

  /**
   * Fetches picnic data records from GraphQL API with graceful fallback to mock service.
   * Supports server-side cursor pagination when isAppendNextPage is set to true.
   *
   * Description:
   *   Builds query filter criteria from component state and executes the GraphQL API query,
   *   populating table rows, KPI summaries, and alert banners.
   *
   * Parameters:
   *   isAppendNextPage: boolean - When true, appends the next page of records using paginationNextCursor.
   *
   * Return Value:
   *   void - No return value; updates tableDataSource and change detection.
   *
   * Exceptions & Errors:
   *   Catches HTTP/GraphQL errors and gracefully falls back to local mock dataset.
   */
  public async loadCuadroPicnic(
    options: {
      resetPage?: boolean;
      cursor?: string | null;
      isFiltering?: boolean;
      append?: boolean;
      targetNeededRows?: number;
    } = {},
  ): Promise<void> {
    // Immediately cancel any previous in-flight load request
    this.cancelPendingLoad$.next();
    const executionId = ++this.activeLoadExecutionId;

    const isAppend = Boolean(options.append);
    const isReset = options.resetPage ?? !isAppend;
    if (isReset) {
      this.pageIndex = 0;
      this.pageCursors = [null];
      this.paginationNextCursor = undefined;
      this.masterPicnicItems = [];
    }

    const showFiltering =
      options.isFiltering ?? this.masterPicnicItems.length > 0;
    this.isFiltering = showFiltering;
    this.isLoadingData = true;
    this.errorMessageText = null;
    this.changeDetectorRef.markForCheck();

    let targetCursor: string | undefined =
      options.cursor !== undefined
        ? options.cursor || undefined
        : this.paginationNextCursor || undefined;

    const formattedStartDate = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.startDate,
    );
    const formattedEndDate = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.endDate,
    );

    this.lastQueriedStartDate = formattedStartDate;
    this.lastQueriedEndDate = formattedEndDate;

    const rawSearch = (this.filterCriteriaRecord.searchKeyword || '').trim();
    const effectiveSearch = this.canSendSearch(rawSearch)
      ? rawSearch
      : undefined;

    const targetNeededRows =
      options.targetNeededRows ?? (this.pageIndex + 1) * this.pageSize;

    let batchIterationCount = 0;

    try {
      while (
        this.masterPicnicItems.length < targetNeededRows &&
        batchIterationCount < this.maxConsecutiveBatches
      ) {
        if (executionId !== this.activeLoadExecutionId) {
          return; // Aborted by a newer query or filter change
        }

        // On subsequent loop iterations, if targetCursor is null/undefined, no more files exist
        if (batchIterationCount > 0 && !targetCursor) {
          break;
        }

        const filterPayload = {
          year:
            this.filterCriteriaRecord.selectedYear ||
            new Date().getFullYear().toString(),
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          searchTerm: effectiveSearch,
          foodType: this.filterCriteriaRecord.selectedServiceType || undefined,
          limit: this.serverBatchLimit,
          exclusiveStartKeyId: targetCursor,
        };

        batchIterationCount++;

        const graphQLResponsePayload = await firstValueFrom(
          this.picnicService.queryGetCuadroPicnic(filterPayload).pipe(
            takeUntil(this.cancelPendingLoad$),
            takeUntil(this._unsubscribeOnDestroy$),
            catchError((apiError) => {
              console.error(
                '[CuadroPicnicComponent] Error querying getCuadroPicnic GraphQL API:',
                apiError,
              );
              return of(null);
            }),
          ),
        );

        if (executionId !== this.activeLoadExecutionId) {
          return; // Aborted by a newer query
        }

        if (!graphQLResponsePayload) {
          if (this.masterPicnicItems.length === 0) {
            this.errorMessageText =
              'No se pudieron cargar los datos de Cuadro Picnic desde la API.';
          }
          break;
        }

        const responseContainer =
          graphQLResponsePayload?.getCuadroPicnicPage ||
          graphQLResponsePayload?.getCuadroPicnic;

        if (responseContainer) {
          const mappedRowsCollection = this.mapGraphQLResponseToItems(
            graphQLResponsePayload,
          );
          this.masterPicnicItems = [
            ...this.masterPicnicItems,
            ...mappedRowsCollection,
          ];

          const nextCursorToken =
            (responseContainer as any).nextCursor ||
            (responseContainer as any).lastEvaluatedKeyId ||
            undefined;

          this.paginationNextCursor = nextCursorToken || undefined;

          if (typeof responseContainer.totalCount === 'number') {
            this.totalServerRecords = responseContainer.totalCount;
          }

          targetCursor = this.paginationNextCursor;

          // If server returned no cursor, end of dataset reached
          if (!this.paginationNextCursor) {
            break;
          }
        } else {
          this.paginationNextCursor = undefined;
          break;
        }
      }
    } catch (unexpectedError) {
      if (executionId !== this.activeLoadExecutionId) {
        return;
      }
      console.error(
        '[CuadroPicnicComponent] Unexpected error in loadCuadroPicnic loop:',
        unexpectedError,
      );
      if (this.masterPicnicItems.length === 0) {
        this.errorMessageText =
          'Error inesperado al recuperar los registros de Cuadro Picnic.';
      }
    }

    if (executionId !== this.activeLoadExecutionId) {
      return;
    }

    if (this.masterPicnicItems.length === 0 && !this.errorMessageText) {
      this.errorMessageText =
        'No se encontraron registros de Cuadro Picnic con los filtros seleccionados.';
    }

    this.configureTableSortingAccessor();

    // Client-side computation of KPIs and Snack warnings strictly for items consulted up to that moment
    this.summaryMetricsRecord = this.computeSummaryMetrics(
      this.masterPicnicItems,
    );
    const computedAlerts = this.computeConsecutiveSnackWarnings(
      this.masterPicnicItems,
    );
    if (!this.activeAlertFilterFileNumber) {
      this.consecutiveSnackAlertList = computedAlerts;
    } else if (computedAlerts.length > 0) {
      const activeWarning = computedAlerts[0];
      const existingWarningIndex = this.consecutiveSnackAlertList.findIndex(
        (warnItem) => warnItem.fileNumber === activeWarning.fileNumber,
      );
      if (existingWarningIndex >= 0) {
        this.consecutiveSnackAlertList[existingWarningIndex] = activeWarning;
      }
    }

    this.isFiltering = false;
    this.isLoadingData = false;
    this.refreshTableSlice();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Configures table sorting accessors for all custom columns in Cuadro Picnic.
   *
   * @returns {void}
   */
  private configureTableSortingAccessor(): void {
    if (this.matSortComponent) {
      this.tableDataSource.sort = this.matSortComponent;
    }

    this.tableDataSource.sortingDataAccessor = (
      itemRow: CuadroPicnicItem,
      sortProperty: string,
    ) => {
      switch (sortProperty) {
        case 'fechaOperacion':
          return itemRow.fechaOperacion;
        case 'fileNumber':
          return itemRow.fileNumber;
        case 'nameClient':
          return itemRow.nameClient;
        case 'nombreItinerario':
          return itemRow.nombreItinerario;
        case 'reservationExecutive':
          return (itemRow.reservationExecutives || []).join(', ');
        case 'guiaPrincipal':
          return itemRow.guiaPrincipal || '';
        case 'tipoAlimento':
          return itemRow.foodType || '';
        case 'activityName':
          return itemRow.activityName || '';
        case 'pax':
          return itemRow.picnicGroupDetails?.activePaxCount || 0;
        case 'tc':
          return itemRow.picnicGroupDetails?.activeTcCount || 0;
        case 'driver':
          return itemRow.picnicGroupDetails?.driverCount || 0;
        case 'guia':
          return itemRow.picnicGroupDetails?.guiaCount || 0;
        case 'asistGuia':
          return itemRow.picnicGroupDetails?.asistGuiaCount || 0;
        case 'fieldStaff':
          return itemRow.picnicGroupDetails?.fieldStaffCount || 0;
        case 'staff':
          return itemRow.picnicGroupDetails?.staffCount || 0;
        case 'total':
          return itemRow.picnicGroupDetails?.totalMesaCount || 0;
        case 'infante':
          return itemRow.picnicGroupDetails?.infantCount || 0;
        case 'nino':
          return itemRow.picnicGroupDetails?.childCount || 0;
        case 'isPicnicOrderReviewed':
          return itemRow.isPicnicOrderReviewed ? 1 : 0;
        case 'revisadoBiblia':
          return itemRow.revisadoBiblia === true
            ? 2
            : itemRow.revisadoBiblia === false
              ? 1
              : 0;
        default:
          return (itemRow as any)[sortProperty] || '';
      }
    };
  }

  /**
   * Toggles the collapse/expansion state of the consecutive snack alert cards.
   *
   * @description Switches between displaying detailed file cards and a compact header banner.
   * @returns {void} No return value.
   * @throws {never} This method does not throw errors.
   */
  public toggleSnackAlertListExpansion(): void {
    this.isSnackAlertListExpanded = !this.isSnackAlertListExpanded;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Dismisses the snack operational alert banner from the active view session.
   *
   * @description Hides the alert banner until the next data reload or filter reset.
   * @returns {void} No return value.
   * @throws {never} This method does not throw errors.
   */
  public dismissSnackAlertBanner(): void {
    this.isSnackAlertBannerDismissed = true;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Filters the table by the selected File Number from the operational alert banner.
   *
   * Description:
   *   Sets the general search keyword to the selected file number and retrieves matching records.
   *
   * Parameters:
   *   fileNumber: string - The file number to filter by.
   *
   * Return Value:
   *   void.
   */
  public filterByFileNumber(fileNumber: string): void {
    if (!fileNumber) {
      return;
    }

    const targetFile = fileNumber.trim();
    if (this.activeAlertFilterFileNumber === targetFile) {
      this.clearAlertFileFilter();
      return;
    }

    this.activeAlertFilterFileNumber = targetFile;
    this.clearPendingSearchDebounce();
    this.filterCriteriaRecord.searchKeyword = targetFile;
    this.searchValidationMessage = '';
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });

    const tableElement = document.querySelector('.table-surface');
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Clears active operational alert file filter and restores complete dataset.
   *
   * @returns {void}
   */
  public clearAlertFileFilter(): void {
    this.activeAlertFilterFileNumber = null;
    this.filterCriteriaRecord.searchKeyword = '';
    this.searchValidationMessage = '';
    this.clearPendingSearchDebounce();
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
  }

  /**
   * Handles food type filter changes from the primary filters bar.
   *
   * @param newType - Selected food type string (e.g. 'SNACK', 'PICNIC', '').
   */
  public onServiceTypeChange(newType?: string): void {
    this.clearPendingSearchDebounce();
    if (newType !== undefined) {
      this.filterCriteriaRecord.selectedServiceType = newType;
    }
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
  }

  /**
   * Computes consecutive snack warnings across all loaded table items on the frontend.
   *
   * Description:
   *   Groups records by fileNumber, sorts snack operation dates chronologically,
   *   and identifies sliding windows of 3 or more consecutive calendar days.
   *   Mutates item.hasConsecutiveSnackAlert and item.consecutiveSnackDates on affected items
   *   and returns deduplicated warnings for the operational banner.
   *
   * Parameters:
   *   itemsList: CuadroPicnicItem[] - Master list of picnic items.
   *
   * Return Value:
   *   ConsecutiveSnackWarning[] - Deduplicated list of warnings by fileNumber.
   */
  public computeConsecutiveSnackWarnings(
    itemsList: CuadroPicnicItem[],
  ): ConsecutiveSnackWarning[] {
    const warningsList: ConsecutiveSnackWarning[] = [];
    if (!itemsList || itemsList.length === 0) {
      return warningsList;
    }

    // Reset alert flags first
    for (const item of itemsList) {
      item.hasConsecutiveSnackAlert = false;
      item.consecutiveSnackDates = [];
    }

    // Group items by fileNumber
    const itemsByFile = new Map<string, CuadroPicnicItem[]>();
    for (const item of itemsList) {
      if (!item.fileNumber) continue;
      const fileKey = item.fileNumber.trim();
      if (!itemsByFile.has(fileKey)) {
        itemsByFile.set(fileKey, []);
      }
      itemsByFile.get(fileKey)!.push(item);
    }

    const ONE_DAY_MS = 24 * 60 * 60 * 1000;

    // Process each file group
    for (const [fileNumber, fileItems] of itemsByFile.entries()) {
      // Find all snack items with a valid fechaOperacion
      const snackItems = fileItems.filter(
        (it) =>
          (it.foodType || '').toUpperCase().trim() === TIPO_ALIMENTO.SNACK &&
          Boolean(it.fechaOperacion),
      );

      if (snackItems.length < 3) {
        continue;
      }

      // Collect unique operation dates sorted chronologically
      const uniqueDates = Array.from(
        new Set(snackItems.map((it) => it.fechaOperacion)),
      ).sort();

      if (uniqueDates.length < 3) {
        continue;
      }

      // Convert to Date timestamps (UTC midnight) to check consecutive calendar days
      const parsedDates = uniqueDates.map((dStr) => {
        const [year, month, day] = dStr.split('-').map(Number);
        return {
          dateStr: dStr,
          timestamp: Date.UTC(year, month - 1, day),
        };
      });

      const matchedConsecutiveDatesSet = new Set<string>();

      // Sliding window to find sequences of >= 3 consecutive days
      let currentRun: string[] = [parsedDates[0].dateStr];

      for (let i = 1; i < parsedDates.length; i++) {
        const diffDays = Math.round(
          (parsedDates[i].timestamp - parsedDates[i - 1].timestamp) /
            ONE_DAY_MS,
        );

        if (diffDays === 1) {
          currentRun.push(parsedDates[i].dateStr);
        } else {
          if (currentRun.length >= 3) {
            currentRun.forEach((d) => matchedConsecutiveDatesSet.add(d));
          }
          currentRun = [parsedDates[i].dateStr];
        }
      }

      if (currentRun.length >= 3) {
        currentRun.forEach((d) => matchedConsecutiveDatesSet.add(d));
      }

      if (matchedConsecutiveDatesSet.size >= 3) {
        const sortedMatchedDates = Array.from(
          matchedConsecutiveDatesSet,
        ).sort();

        // Mark items belonging to these consecutive dates
        for (const item of snackItems) {
          if (matchedConsecutiveDatesSet.has(item.fechaOperacion)) {
            item.hasConsecutiveSnackAlert = true;
            item.consecutiveSnackDates = sortedMatchedDates;
          }
        }

        const representativeItem = fileItems[0];
        warningsList.push({
          fileNumber,
          clientName: representativeItem.nameClient || 'Cliente',
          nombreItinerario: representativeItem.nombreItinerario || '',
          reservationExecutives: representativeItem.reservationExecutives || [],
          consecutiveDates: sortedMatchedDates,
          warningMessage: `Alerta: File ${fileNumber} (${representativeItem.nameClient || 'Cliente'}) presenta servicios de SNACK por 3 o más días seguidos.`,
        });
      }
    }

    return warningsList;
  }

  /**
   * Slices the master picnic items buffer for the current page and updates tableDataSource.
   * Ensures pageIndex remains within valid bounds and marks view for change detection.
   *
   * @returns {void}
   */
  public refreshTableSlice(): void {
    const totalItems = this.masterPicnicItems.length;
    const maxPageIndex = Math.max(0, Math.ceil(totalItems / this.pageSize) - 1);
    if (this.pageIndex > maxPageIndex) {
      this.pageIndex = maxPageIndex;
    }
    if (
      this.matPaginatorComponent &&
      this.matPaginatorComponent.pageIndex !== this.pageIndex
    ) {
      this.matPaginatorComponent.pageIndex = this.pageIndex;
    }
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.tableDataSource.data = this.masterPicnicItems.slice(
      startIndex,
      endIndex,
    );
    if (this.paginatorIntl) {
      this.paginatorIntl.changes.next();
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Toggles the visibility of the quick legend panel.
   *
   * Description:
   *   Inverts the isLegendExpanded boolean flag and requests change detection.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleLegend(): void {
    this.isLegendExpanded = !this.isLegendExpanded;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Clears the current search keyword and reloads data from backend.
   *
   * @param {Event} clickEvent - Optional click event to stop propagation.
   * @returns {void}
   */
  public clearSearchKeyword(clickEvent?: Event): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }
    this.activeAlertFilterFileNumber = null;
    this.clearPendingSearchDebounce();
    this.filterCriteriaRecord.searchKeyword = '';
    this.searchValidationMessage = '';
    this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Returns the count of actively applied filters.
   *
   * @returns {number} Count of active filter fields.
   */
  public get activeFiltersCount(): number {
    let activeFiltersTotal = 0;
    if (
      this.filterCriteriaRecord.selectedYear &&
      this.filterCriteriaRecord.selectedYear !==
        new Date().getFullYear().toString()
    ) {
      activeFiltersTotal++;
    }
    if (this.filterCriteriaRecord.searchKeyword?.trim()) {
      activeFiltersTotal++;
    }
    if (this.filterCriteriaRecord.selectedServiceType) {
      activeFiltersTotal++;
    }
    if (
      this.filterCriteriaRecord.startDate ||
      this.filterCriteriaRecord.endDate
    ) {
      activeFiltersTotal++;
    }
    return activeFiltersTotal;
  }

  /**
   * Generates a structured list of active filter chips for display in the filters footer.
   *
   * @returns {Array<{ key: string; label: string; value: string }>} List of active chip objects.
   */
  public get activeFilterChips(): Array<{
    key: string;
    label: string;
    value: string;
  }> {
    const chipItemsList: Array<{ key: string; label: string; value: string }> =
      [];

    if (this.filterCriteriaRecord.selectedYear) {
      chipItemsList.push({
        key: 'year',
        label: 'Año',
        value: this.filterCriteriaRecord.selectedYear,
      });
    }

    if (this.filterCriteriaRecord.searchKeyword?.trim()) {
      chipItemsList.push({
        key: 'search',
        label: 'Búsqueda',
        value: `"${this.filterCriteriaRecord.searchKeyword.trim()}"`,
      });
    }

    if (this.filterCriteriaRecord.selectedServiceType) {
      chipItemsList.push({
        key: 'serviceType',
        label: 'Tipo',
        value: this.filterCriteriaRecord.selectedServiceType,
      });
    }

    if (
      this.filterCriteriaRecord.startDate ||
      this.filterCriteriaRecord.endDate
    ) {
      const rangeLabel = formatDateRangeFilterLabel(
        this.filterCriteriaRecord.startDate,
        this.filterCriteriaRecord.endDate,
      );

      chipItemsList.push({
        key: 'dateRange',
        label: 'Fechas',
        value: rangeLabel,
      });
    }

    return chipItemsList;
  }

  /**
   * Removes a single filter by its key identifier.
   *
   * Description:
   *   Clears the specified filter criteria (year, search, service type, date range) and reloads data.
   *
   * Parameters:
   *   filterKey: string - Identifier of the filter to remove ('year', 'search', 'serviceType', 'dateRange').
   *
   * Return Value:
   *   void.
   */
  public removeFilter(filterKey: string): void {
    this.clearPendingSearchDebounce();
    if (filterKey === 'year') {
      this.filterCriteriaRecord.selectedYear = new Date()
        .getFullYear()
        .toString();
      this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    } else if (filterKey === 'search') {
      this.clearSearchKeyword();
    } else if (filterKey === 'serviceType') {
      this.filterCriteriaRecord.selectedServiceType = '';
      this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    } else if (filterKey === 'dateRange') {
      this.filterCriteriaRecord.startDate = null;
      this.filterCriteriaRecord.endDate = null;
      this.loadCuadroPicnic({ resetPage: true, isFiltering: true });
    }
  }

  /**
   * Resets all applied filter criteria to default state.
   *
   * Description:
   *   Clears search keyword, selected service type, resets year to current year, and clears dates.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   */
  public resetTableFilters(): void {
    this.activeAlertFilterFileNumber = null;
    this.clearPendingSearchDebounce();
    this.filterCriteriaRecord.searchKeyword = '';
    this.searchValidationMessage = '';
    this.filterCriteriaRecord.selectedServiceType = '';
    this.filterCriteriaRecord.selectedYear = new Date()
      .getFullYear()
      .toString();
    this.filterCriteriaRecord.startDate = null;
    this.filterCriteriaRecord.endDate = null;
    this.filterCriteriaRecord.exclusiveStartKeyId = undefined;
    this.loadCuadroPicnic({ resetPage: true, isFiltering: false });
  }

  /**
   * Opens the Picnic Details modal (Rows 9–17 breakdown + Staff).
   *
   * @description Displays details modal for Pax, Tc, Driver, Guía, Asist Guía, Staff, and Restrictions.
   * @param {CuadroPicnicItem} targetRowItem - The selected row item record.
   * @returns {void}
   */
  public openPicnicDetailsModal(targetRowItem: CuadroPicnicItem): void {
    const dialogConfigObject = new MatDialogConfig();
    dialogConfigObject.width = '780px';
    dialogConfigObject.maxWidth = '95vw';
    dialogConfigObject.maxHeight = '90vh';
    dialogConfigObject.panelClass = 'picnic-details-custom-dialog-panel';
    dialogConfigObject.autoFocus = false;
    dialogConfigObject.data = {
      picnicItemRow: targetRowItem,
      idActivity: targetRowItem.idActivity,
      idItinerary: targetRowItem.idItinerary,
      foodType: targetRowItem.foodType,
      paxCount: targetRowItem.picnicGroupDetails?.activePaxCount ?? 0,
      tcCount: targetRowItem.picnicGroupDetails?.activeTcCount ?? 0,
    };

    this.dialogService.open(DialogPicnicDetailsComponent, dialogConfigObject);
  }

  /**
   * Opens the Operational Services modal (Emp. Transporte, Asistentes de Picnic, Bañero).
   *
   * Description:
   *   Opens the operational services dialog for the chosen category and synchronizes the updated OS
   *   summary metrics across all table rows sharing the same activity identifier upon modal closure.
   *
   * Parameters:
   *   targetRowItem: CuadroPicnicItem - The selected row item record.
   *   modalType: OperationalServiceModalType - Category or modal type identifier.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public openOperationalServicesModal(
    targetRowItem: CuadroPicnicItem,
    modalType: OperationalServiceModalType,
  ): void {
    const { title: customTitle, icon: customIcon } =
      resolveOperationalServiceMetadata(modalType);

    const dialogConfigObject = new MatDialogConfig();
    dialogConfigObject.width = '780px';
    dialogConfigObject.maxWidth = '95vw';
    dialogConfigObject.autoFocus = false;
    dialogConfigObject.disableClose = true;
    dialogConfigObject.data = {
      picnicItemRow: targetRowItem,
      modalType,
      customTitle,
      customIcon,
    };

    const dialogRef = this.dialogService.open(
      ServiciosOperativosModalComponent,
      dialogConfigObject,
    );

    dialogRef
      .afterClosed()
      .subscribe(
        (dialogResult?: {
          updatedSummary?: PicnicOperationalServiceSummary;
          modalType?: OperationalServiceModalType;
        }) => {
          if (dialogResult?.updatedSummary && dialogResult?.modalType) {
            this.masterPicnicItems = synchronizeOperationalServiceSummary(
              this.masterPicnicItems,
              targetRowItem,
              dialogResult.modalType,
              dialogResult.updatedSummary,
            );
          }
          this.refreshTableSlice();
          this.changeDetectorRef.detectChanges();
        },
      );
  }

  /**
   * Backward-compatible alias for openOperationalServicesModal.
   */
  public openServiciosOperativosModal(
    targetRowItem: CuadroPicnicItem,
    modalType: OperationalServiceModalType,
  ): void {
    this.openOperationalServicesModal(targetRowItem, modalType);
  }

  /**
   * Resolves the CSS status class for a service count pill based on OS generation completion.
   * Delegated to shared operational-services utility.
   *
   * @param serviceSummary - Operational service summary containing totalCount and generatedOrdersCount.
   * @returns CSS modifier class ('os-status-complete', 'os-status-partial', 'os-status-pending', or '').
   */
  public resolveServicePillStatusClass(
    serviceSummary: PicnicOperationalServiceSummary | null | undefined,
  ): string {
    return resolveServicePillStatusClass(serviceSummary);
  }

  public isPillFullyConfirmed(
    serviceSummary: PicnicOperationalServiceSummary | null | undefined,
  ): boolean {
    return isOperationalSummaryFullyConfirmed(serviceSummary);
  }

  public hasPillPreliminary(
    serviceSummary: PicnicOperationalServiceSummary | null | undefined,
  ): boolean {
    return hasOperationalSummaryPreliminary(serviceSummary);
  }

  /**
   * Resolves descriptive tooltip text for the service count pill.
   * Delegated to shared operational-services utility.
   *
   * @param categoryName - Human readable category name.
   * @param serviceSummary - Operational service summary containing counts.
   * @returns Contextual tooltip description.
   */
  public resolveServicePillTooltip(
    categoryName: string,
    serviceSummary: PicnicOperationalServiceSummary | null | undefined,
  ): string {
    return resolveServicePillTooltip(categoryName, serviceSummary);
  }

  /**
   * Opens the multi-service provider assignment dialog for a specific service.
   *
   * @param targetServiceItem - Target service item to update provider for.
   * @returns {void}
   */
  public handleEditServiceSupplier(
    targetServiceItem: PicnicServiceDetailItem,
  ): void {
    if (!targetServiceItem || !targetServiceItem.id) {
      this.snackBarNotificationService.open(
        'El servicio no tiene un identificador válido para actualizar',
        'Cerrar',
        { duration: 3000 },
      );
      return;
    }

    const providerDialogReference = this.dialogService.open(
      DialogProviderMultipleComponent,
      {
        width: '480px',
        maxWidth: '90vw',
        disableClose: true,
        data: {
          filtered: [
            {
              id: targetServiceItem.id,
              nombreServicio: targetServiceItem.nombreServicio,
              tipoServicio: targetServiceItem.tipoServicio,
              categoriaServicio: targetServiceItem.categoriaServicio,
              locacion:
                targetServiceItem.locacion ||
                targetServiceItem.serviceSnapshot?.locacion ||
                '',
              serviceSnapshot: targetServiceItem.serviceSnapshot,
              nombreProveedor: targetServiceItem.nombreProveedor,
            },
          ],
          modalTitle: `Cambiar Proveedor: ${targetServiceItem.nombreServicio || targetServiceItem.tipoServicio}`,
        },
      },
    );

    providerDialogReference.afterClosed().subscribe((dialogResult) => {
      if (dialogResult && dialogResult.success) {
        if (
          dialogResult.successfulServiceIds &&
          !dialogResult.successfulServiceIds.includes(targetServiceItem.id)
        ) {
          return;
        }

        const updatedProviderName =
          dialogResult.nombreProveedor || dialogResult.providerName || '';
        const updatedProviderId =
          dialogResult.idProveedor || dialogResult.providerId || '';

        if (updatedProviderName) {
          targetServiceItem.nombreProveedor = updatedProviderName;
        }
        if (updatedProviderId) {
          targetServiceItem.idProveedor = updatedProviderId;
        }
        this.refreshTableSlice();
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Evaluates if a specific row item's order review status is currently updating in the background.
   *
   * @param rowId - Unique row composite identifier.
   * @returns {boolean} True if updating, false otherwise.
   */
  public isOrderReviewRowUpdating(rowId: string): boolean {
    return this.updatingRowReviewIdentifiersSet.has(rowId);
  }

  /**
   * Indicates if any row review mutation is actively processing across the table.
   *
   * @returns {boolean} True if at least one row is updating.
   */
  public get isAnyOrderReviewRowUpdating(): boolean {
    return this.updatingRowReviewIdentifiersSet.size > 0;
  }

  /**
   * Generates a descriptive tooltip for the Revisado Pedido checkbox column.
   *
   * Description:
   *   Builds tooltip text indicating whether the order is reviewed and approved,
   *   including the reviewer email and timestamp if available.
   *
   * Parameters:
   *   activityFoodItem: CuadroPicnicItem - Target row item record.
   *
   * Return Value:
   *   string - Human-readable tooltip string.
   *
   * Exceptions & Errors:
   *   None.
   */
  public getOrderReviewTooltip(activityFoodItem: CuadroPicnicItem): string {
    if (activityFoodItem.isPicnicOrderReviewed) {
      if (activityFoodItem.reviewedBy) {
        const timestampSuffix = activityFoodItem.reviewedAt
          ? ` (${activityFoodItem.reviewedAt})`
          : '';
        return `Revisado y aprobado por ${activityFoodItem.reviewedBy}${timestampSuffix} (Clic para cambiar a pendiente)`;
      }
      return 'Revisado y aprobado (Clic para cambiar a pendiente)';
    }
    return 'Pendiente de revisión (Clic para confirmar)';
  }

  /**
   * Handles user interaction for toggling the programming review status ('Revisado Pedido').
   *
   * Description:
   *   Prompts the user with a SweetAlert2 confirmation dialog.
   *   Upon confirmation, obtains the authenticated user email from CognitoService,
   *   blocks other checkboxes with an in-flight loader on the cell,
   *   executes the mutationSetPicnicOrderReviewed GraphQL mutation against api-explorandes-core,
   *   updates the row state, and provides clear feedback toasts.
   *
   * Parameters:
   *   targetRowItem: CuadroPicnicItem - Target row item record.
   *   targetBooleanState: boolean - Desired boolean state (true = reviewed, false = pending).
   *
   * Return Value:
   *   Promise<void> - Resolves when the user flow completes.
   *
   * Exceptions & Errors:
   *   Displays warning/error alerts if validation fails or the network mutation fails.
   */
  public async handleTogglePicnicOrderReview(
    targetRowItem: CuadroPicnicItem,
    targetBooleanState: boolean,
  ): Promise<void> {
    if (!targetRowItem || this.isOrderReviewRowUpdating(targetRowItem.id)) {
      return;
    }

    const targetActivityIdentifier =
      targetRowItem.idActivity || targetRowItem.id?.split('_')?.[1];
    const targetFoodTypeKey = targetRowItem.foodType || 'SNACK';
    const targetFileNumber = targetRowItem.fileNumber || 'File';

    if (!targetActivityIdentifier) {
      void Swal.fire({
        icon: 'error',
        title: ORDER_REVIEW_MESSAGES.errorIdentifierTitle,
        text: ORDER_REVIEW_MESSAGES.errorIdentifierText,
        confirmButtonColor: '#eb6b31',
      });
      return;
    }

    const actionDescriptionText = targetBooleanState
      ? ORDER_REVIEW_MESSAGES.actionReviewedHtml
      : ORDER_REVIEW_MESSAGES.actionPendingHtml;

    const confirmationResult = await Swal.fire({
      title: targetBooleanState
        ? ORDER_REVIEW_MESSAGES.confirmTitleReviewed
        : ORDER_REVIEW_MESSAGES.confirmTitlePending,
      html: `¿Está seguro de que desea ${actionDescriptionText} el pedido de <strong>${targetFoodTypeKey}</strong> para el file <strong>${targetFileNumber}</strong>?`,
      icon: targetBooleanState ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonColor: targetBooleanState ? '#15803d' : '#eb6b31',
      cancelButtonColor: '#71717a',
      confirmButtonText: targetBooleanState
        ? ORDER_REVIEW_MESSAGES.confirmButtonReviewed
        : ORDER_REVIEW_MESSAGES.confirmButtonPending,
      cancelButtonText: ORDER_REVIEW_MESSAGES.cancelButton,
      reverseButtons: true,
    });

    if (!confirmationResult.isConfirmed) {
      return;
    }

    let authenticatedReviewerEmail: string | undefined = undefined;
    try {
      authenticatedReviewerEmail =
        await this.cognitoService.getAuthenticatedUserEmail();
    } catch (authResolutionError: unknown) {
      console.warn(
        '[CuadroPicnicComponent] Could not resolve authenticated user email from Cognito:',
        authResolutionError,
      );
    }

    const rowIdentifier = targetRowItem.id;
    this.updatingRowReviewIdentifiersSet.add(rowIdentifier);
    this.changeDetectorRef.markForCheck();

    this.picnicService
      .mutationSetPicnicOrderReviewed({
        idActivity: targetActivityIdentifier,
        tipoAlimento: targetFoodTypeKey,
        isReviewed: targetBooleanState,
        reviewedBy: authenticatedReviewerEmail,
      })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: (mutationResultEnvelope) => {
          this.updatingRowReviewIdentifiersSet.delete(rowIdentifier);
          const reviewResultPayload =
            mutationResultEnvelope?.setPicnicOrderReviewed;

          if (
            reviewResultPayload &&
            typeof reviewResultPayload.isReviewed === 'boolean'
          ) {
            targetRowItem.isPicnicOrderReviewed =
              reviewResultPayload.isReviewed;
            targetRowItem.reviewedAt = reviewResultPayload.reviewedAt;
            targetRowItem.reviewedBy =
              reviewResultPayload.reviewedBy || authenticatedReviewerEmail;
            this.changeDetectorRef.markForCheck();

            void Swal.fire({
              icon: 'success',
              title: targetBooleanState
                ? ORDER_REVIEW_MESSAGES.successTitleReviewed
                : ORDER_REVIEW_MESSAGES.successTitlePending,
              text: `El pedido de ${targetFoodTypeKey} (${targetFileNumber}) se actualizó correctamente.`,
              toast: true,
              position: 'top-end',
              timer: 2500,
              showConfirmButton: false,
            });
          } else {
            this.changeDetectorRef.markForCheck();
            void Swal.fire({
              icon: 'error',
              title: ORDER_REVIEW_MESSAGES.errorUpdateTitle,
              text: ORDER_REVIEW_MESSAGES.errorUpdateText,
              confirmButtonColor: '#eb6b31',
            });
          }
        },
        error: (mutationNetworkError: unknown) => {
          this.updatingRowReviewIdentifiersSet.delete(rowIdentifier);
          this.changeDetectorRef.markForCheck();
          console.error(
            '[CuadroPicnicComponent] Error executing setPicnicOrderReviewed mutation:',
            mutationNetworkError,
          );
          void Swal.fire({
            icon: 'error',
            title: ORDER_REVIEW_MESSAGES.errorConnectionTitle,
            text: ORDER_REVIEW_MESSAGES.errorConnectionText,
            confirmButtonColor: '#eb6b31',
          });
        },
      });
  }

  /**
   * Set tracking row IDs with expanded dietary restrictions chips.
   */
  public readonly expandedRestrictionsRowIdentifiersSet = new Set<string>();

  /**
   * Set tracking row IDs with expanded observation cards.
   */
  public readonly expandedObservationRowIdentifiersSet = new Set<string>();

  /**
   * Filters a list of dietary restrictions returning only valid, meaningful passenger restriction entries.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawRestrictionsList - Collection of raw dietary restriction strings.
   * @returns Sanitized array containing only active dietary restrictions.
   */
  public getValidDietaryRestrictions(
    rawRestrictionsList: string[] | undefined,
  ): string[] {
    return filterValidDietaryRestrictions(rawRestrictionsList);
  }

  /**
   * Extracts unique, distinct dietary restriction tag labels across all passengers in a row.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawRestrictionsList - Raw restriction strings from backend.
   * @returns Distinct list of clean restriction tag names (e.g. ['Vegetariano', 'Celiaco/Gluten free']).
   */
  public getDistinctDietaryRestrictionTags(
    rawRestrictionsList: string[] | undefined,
  ): string[] {
    return getDistinctDietaryRestrictionTags(rawRestrictionsList);
  }

  /**
   * Parses raw dietary restrictions into structured passenger details.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawRestrictionsList - Collection of raw dietary restriction strings.
   * @returns Array of structured passenger restriction records.
   */
  public getParsedPassengerRestrictions(
    rawRestrictionsList: string[] | undefined,
  ): PassengerDietaryRestrictionDetail[] {
    return parsePassengerDietaryRestrictions(rawRestrictionsList);
  }

  /**
   * Extracts the passenger name portion from a formatted passenger restriction string.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawPassengerLine - Formatted passenger restriction line.
   * @returns Passenger name prefix.
   */
  public getPassengerNamePrefix(rawPassengerLine: string): string {
    return extractPassengerNamePrefix(rawPassengerLine);
  }

  /**
   * Extracts the dietary restrictions text portion from a formatted passenger restriction string.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawPassengerLine - Formatted passenger restriction line.
   * @returns Dietary restrictions text.
   */
  public getPassengerRestrictionsBody(rawPassengerLine: string): string {
    return extractPassengerRestrictionsBody(rawPassengerLine);
  }

  /**
   * Determines whether a passenger restriction line contains critical conditions (allergies or celiac/gluten-free).
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawPassengerLine - Formatted passenger restriction line.
   * @returns True if critical allergy or celiac condition exists, false otherwise.
   */
  public isCriticalRestriction(rawPassengerLine: string): boolean {
    return isCriticalDietaryRestriction(rawPassengerLine);
  }

  /**
   * Resolves CSS modifier class for a specific restriction badge.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param restrictionTag - Formatted restriction label.
   * @returns CSS modifier class name.
   */
  public getRestrictionBadgeClass(restrictionTag: string): string {
    return getDietaryBadgeClass(restrictionTag);
  }

  /**
   * Resolves Tabler icon name appropriate for a specific restriction badge.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param restrictionTag - Formatted restriction label.
   * @returns Tabler icon identifier string.
   */
  public getRestrictionBadgeIcon(restrictionTag: string): string {
    return getDietaryBadgeIcon(restrictionTag);
  }

  /**
   * Evaluates whether a row item contains at least one active, valid dietary restriction.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawRestrictionsList - Collection of raw dietary restriction strings.
   * @returns True if active dietary restrictions exist; false otherwise.
   */
  public hasValidDietaryRestrictions(
    rawRestrictionsList: string[] | undefined,
  ): boolean {
    return hasValidDietaryRestrictions(rawRestrictionsList);
  }

  /**
   * Formats a clean, multi-line tooltip string presenting all active dietary restrictions for a group.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param rawRestrictionsList - Collection of raw dietary restriction strings.
   * @returns Human-readable formatted multi-line string for matTooltip.
   */
  public formatDietaryRestrictionsTooltip(
    rawRestrictionsList: string[] | undefined,
  ): string {
    return formatDietaryRestrictionsTooltipText(rawRestrictionsList);
  }

  /**
   * Checks whether the dietary restrictions for a specific row are expanded inline.
   *
   * Description:
   *   Checks membership in expandedRestrictionsRowIdentifiersSet.
   *
   * Parameters:
   *   rowIdentifier: string - Unique row identifier.
   *
   * Return Value:
   *   boolean - True if expanded, false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isRestrictionsExpanded(rowIdentifier: string): boolean {
    return this.expandedRestrictionsRowIdentifiersSet.has(rowIdentifier);
  }

  /**
   * Toggles inline expanded state of dietary restrictions for a specific row.
   *
   * Description:
   *   Adds or removes row ID from expandedRestrictionsRowIdentifiersSet.
   *
   * Parameters:
   *   rowIdentifier: string - Unique row identifier.
   *   clickEvent: Event (optional) - Mouse or button click event to stop propagation.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleRestrictionsExpanded(
    rowIdentifier: string,
    clickEvent?: Event,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }
    if (this.expandedRestrictionsRowIdentifiersSet.has(rowIdentifier)) {
      this.expandedRestrictionsRowIdentifiersSet.delete(rowIdentifier);
    } else {
      this.expandedRestrictionsRowIdentifiersSet.add(rowIdentifier);
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Checks whether an observation text is long enough to require a 'ver más' toggle.
   *
   * Description:
   *   Evaluates string length against a threshold of 60 characters.
   *
   * Parameters:
   *   observationText: string | null | undefined - Raw observation string.
   *
   * Return Value:
   *   boolean - True if length exceeds 60 characters.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isObservationLong(
    observationText: string | null | undefined,
  ): boolean {
    return Boolean(observationText && observationText.trim().length > 60);
  }

  /**
   * Checks whether the observation card for a specific row is expanded inline.
   *
   * Description:
   *   Checks membership in expandedObservationRowIdentifiersSet.
   *
   * Parameters:
   *   rowIdentifier: string - Unique row identifier.
   *
   * Return Value:
   *   boolean - True if expanded, false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isObservationExpanded(rowIdentifier: string): boolean {
    return this.expandedObservationRowIdentifiersSet.has(rowIdentifier);
  }

  /**
   * Toggles inline expanded state of the observation card for a specific row.
   *
   * Description:
   *   Adds or removes row ID from expandedObservationRowIdentifiersSet.
   *
   * Parameters:
   *   rowIdentifier: string - Unique row identifier.
   *   clickEvent: Event (optional) - Mouse or button click event to stop propagation.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleObservationExpanded(
    rowIdentifier: string,
    clickEvent?: Event,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }
    if (this.expandedObservationRowIdentifiersSet.has(rowIdentifier)) {
      this.expandedObservationRowIdentifiersSet.delete(rowIdentifier);
    } else {
      this.expandedObservationRowIdentifiersSet.add(rowIdentifier);
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Transforms raw GraphQL response rows to CuadroPicnicItem domain entities.
   *
   * Description:
   *   Normalizes GraphQL data into strongly typed CuadroPicnicItem objects with default fallback structures.
   *
   * Parameters:
   *   graphQLResponsePayload: any - Raw GraphQL response object from getCuadroPicnic query.
   *
   * Return Value:
   *   CuadroPicnicItem[] - Array of formatted CuadroPicnicItem entities ready for MatTableDataSource consumption.
   *
   * Exceptions & Errors:
   */
  private mapGraphQLResponseToItems(
    graphQLResponsePayload: any,
  ): CuadroPicnicItem[] {
    const rawItems =
      graphQLResponsePayload?.getCuadroPicnicPage?.items ||
      graphQLResponsePayload?.getCuadroPicnic?.items ||
      [];

    const filterStart = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.startDate,
    );
    const filterEnd = this.formatLocalDateToYMD(
      this.filterCriteriaRecord.endDate,
    );

    const filteredRawItems = rawItems.filter((rawRow: any) => {
      const rowDate = (rawRow?.fechaOperacion || '').trim().substring(0, 10);
      if (filterStart && rowDate && rowDate < filterStart) {
        return false;
      }
      if (filterEnd && rowDate && rowDate > filterEnd) {
        return false;
      }
      return true;
    });

    return filteredRawItems.map((rawRow: any) => {
      const picnicDetails = rawRow.picnicDetails || {};
      // Calculate totalPersonas on client: pax + tc + driver + guia + asistGuia + fieldStaff + staff
      const totalPersonas =
        (picnicDetails.paxCount || 0) +
        (picnicDetails.tcCount || 0) +
        (picnicDetails.driverCount || 0) +
        (picnicDetails.guiaCount || 0) +
        (picnicDetails.asistGuiaCount || 0) +
        (picnicDetails.fieldStaffCount || 0) +
        (picnicDetails.staffCount || 0);

      const picnicGroupDetails: PicnicDetailsGroup = {
        activePaxCount: picnicDetails.paxCount || 0,
        activeTcCount: picnicDetails.tcCount || 0,
        driverCount: picnicDetails.driverCount || 0,
        guiaCount: picnicDetails.guiaCount || 0,
        asistGuiaCount: picnicDetails.asistGuiaCount || 0,
        fieldStaffCount: picnicDetails.fieldStaffCount || 0,
        staffCount: picnicDetails.staffCount || 0,
        totalMesaCount: totalPersonas,
        infantCount: picnicDetails.infantCount || 0,
        childCount: picnicDetails.childCount || 0,
        dietaryRestrictionsList: picnicDetails.dietaryRestrictionsSummary || [],
      };

      return {
        id:
          rawRow.id ||
          `${rawRow.idActivity || 'act'}-${rawRow.foodType || 'food'}`,
        idActivity: rawRow.idActivity,
        idItinerary: rawRow.idItinerary,
        fechaOperacion: rawRow.fechaOperacion || '',
        fileNumber: rawRow.fileNumber || '',
        nameClient: rawRow.nameClient || '',
        nombreItinerario: rawRow.nombreItinerario || '',
        isSSFFPadre: rawRow.isSSFFPadre === true,
        reservationExecutives: rawRow.reservationExecutives || [],
        guiaPrincipal: rawRow.guiaPrincipal || '',
        foodType: rawRow.foodType || '',
        activityName: rawRow.activityName || rawRow.servicio || '',
        servicioNombre: rawRow.activityName || rawRow.servicio || '',
        picnicGroupDetails,
        empresaTransporte: rawRow.empresaTransporte || {
          totalCount: 0,
          generatedOrdersCount: 0,
          pendingOrdersCount: 0,
          preliminaryOrdersCount: 0,
        },
        asistentePicnicServices: rawRow.asistentesPicnic || {
          totalCount: 0,
          generatedOrdersCount: 0,
          pendingOrdersCount: 0,
          preliminaryOrdersCount: 0,
        },
        baneroService: rawRow.banero || {
          totalCount: 0,
          generatedOrdersCount: 0,
          pendingOrdersCount: 0,
          preliminaryOrdersCount: 0,
        },
        operadorPicnicService: rawRow.operadorPicnic || {
          totalCount: 0,
          generatedOrdersCount: 0,
          pendingOrdersCount: 0,
          preliminaryOrdersCount: 0,
        },
        isPicnicOrderReviewed: Boolean(rawRow.isPicnicOrderReviewed),
        reviewedAt: null,
        reviewedBy: null,
        revisadoBiblia: rawRow.revisadoBiblia ?? null,
        observacionProgramadora: rawRow.observacionProgramadora || '',
        hasConsecutiveSnackAlert: false,
        consecutiveSnackDates: [],
        totalPersonas,
      };
    });
  }

  /**
   * Computes top header KPI metrics from a list of picnic items.
   *
   * Description:
   *   Aggregates counts of snacks, picnics, box lunches, trek meals, and donativos across loaded table rows.
   *
   * Parameters:
   *   itemsList: CuadroPicnicItem[] - Array of CuadroPicnicItem entities to aggregate.
   *
   * Return Value:
   *   CuadroPicnicSummaryMetrics - Computed summary metrics object.
   *
   * Exceptions & Errors:
   *   None.
   */
  private computeSummaryMetrics(
    itemsList: CuadroPicnicItem[],
  ): CuadroPicnicSummaryMetrics {
    const computedMetricsSummary: CuadroPicnicSummaryMetrics = {
      snackCount: 0,
      picnicCount: 0,
      boxLunchCount: 0,
      trekInkaTrailCount: 0,
      trekAusangateCount: 0,
      trekSalkantayCount: 0,
      hiddenValleyCount: 0,
      donativoCount: 0,
    };

    for (const picnicItem of itemsList || []) {
      const normalizedFoodType = (picnicItem.foodType || '')
        .toUpperCase()
        .trim();
      if (normalizedFoodType === TIPO_ALIMENTO.SNACK) {
        computedMetricsSummary.snackCount++;
      } else if (normalizedFoodType === TIPO_ALIMENTO.PICNIC) {
        computedMetricsSummary.picnicCount++;
      } else if (normalizedFoodType === TIPO_ALIMENTO.BOX_LUNCH) {
        computedMetricsSummary.boxLunchCount++;
      } else if (normalizedFoodType === TIPO_ALIMENTO.ALIM_TREK) {
        computedMetricsSummary.trekInkaTrailCount++;
      } else if (normalizedFoodType === TIPO_ALIMENTO.DONATIVO) {
        computedMetricsSummary.donativoCount++;
      }
    }

    return computedMetricsSummary;
  }
}
